import { useCallback } from 'react'
import {
  ListTodo,
  Calendar as CalendarIcon,
  Kanban as KanbanIcon,
  LayoutPanelLeft,
  CheckSquare,
  Copy,
  Trash2,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { motion, AnimatePresence } from 'framer-motion'
import { useIsTinyScreen } from '@/hooks/use-tiny-screen'
const TABS = [
  {
    id: 'list',
    label: 'Tasks',
    icon: ListTodo,
  },
  {
    id: 'calendar',
    label: 'Calendar',
    icon: CalendarIcon,
  },
  {
    id: 'kanban',
    label: 'Kanban',
    icon: KanbanIcon,
  },
]
const MobileNavItem = ({ id, icon: Icon, label, isActive, onSelect, isTinyScreen }) => {
  const handleClick = useCallback(() => {
    onSelect(id)
  }, [onSelect, id])
  return (
    <button
      onClick={handleClick}
      className={cn(
        'flex flex-col items-center gap-1.5 p-2 rounded-xl transition-[opacity,transform] duration-300 relative group',
        isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {isActive && (
        <motion.div
          layoutId="mobile-nav-indicator"
          className="absolute inset-0 bg-primary/10 rounded-xl -z-10"
          initial={false}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30,
          }}
        />
      )}
      <Icon
        className={cn(
          'transition-transform duration-300',
          isTinyScreen ? 'h-5 w-5' : 'h-6 w-6',
          isActive && 'scale-110',
        )}
      />
      <span className="text-[10px] font-medium tracking-tight">{label}</span>
    </button>
  )
}
const SelectionCount = ({ value, label }) => (
  <div className="flex flex-col items-center gap-1.5 p-2 min-w-[64px]">
    <span className="text-sm font-bold text-primary tabular-nums">{value}</span>
    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
      {label}
    </span>
  </div>
)
const BulkActionButton = ({ onClick, icon: Icon, label, isTinyScreen, className, title }) => (
  <button
    onClick={onClick}
    className={cn(
      'flex flex-col items-center gap-1.5 p-2 rounded-xl transition-[opacity,transform] active:translate-y-px',
      className,
    )}
    title={title}
  >
    <Icon className={cn(isTinyScreen ? 'h-5 w-5' : 'h-6 w-6')} />
    <span className="text-[10px] font-medium tracking-tight">{label}</span>
  </button>
)
const TaskSelectionBar = ({
  selectedCount,
  isTinyScreen,
  markLabel,
  onBulkComplete,
  onBulkDuplicate,
  onBulkDelete,
  onClearSelection,
  markTitle,
}) => (
  <motion.div
    key="task-selection"
    initial={{
      opacity: 0,
      y: 5,
    }}
    animate={{
      opacity: 1,
      y: 0,
    }}
    exit={{
      opacity: 0,
      y: 5,
    }}
    transition={{
      type: 'spring',
      stiffness: 700,
      damping: 40,
    }}
    className="flex justify-around items-center w-full"
  >
    <SelectionCount value={selectedCount} label="Selected" />
    <BulkActionButton
      onClick={onBulkComplete}
      icon={CheckSquare}
      label={markLabel}
      isTinyScreen={isTinyScreen}
      className="text-muted-foreground hover:text-primary"
      title={markTitle}
    />
    <BulkActionButton
      onClick={onBulkDuplicate}
      icon={Copy}
      label="Duplicate"
      isTinyScreen={isTinyScreen}
      className="text-muted-foreground hover:text-primary"
    />
    <BulkActionButton
      onClick={onBulkDelete}
      icon={Trash2}
      label="Delete"
      isTinyScreen={isTinyScreen}
      className="text-destructive hover:bg-destructive/10"
    />
    <BulkActionButton
      onClick={onClearSelection}
      icon={X}
      label="Cancel"
      isTinyScreen={isTinyScreen}
      className="text-muted-foreground hover:bg-muted"
    />
  </motion.div>
)
const ListSelectionBar = ({
  selectedCount,
  isTinyScreen,
  onBulkDuplicate,
  onBulkDelete,
  onClearSelection,
}) => (
  <motion.div
    key="list-selection"
    initial={{
      opacity: 0,
      y: 5,
    }}
    animate={{
      opacity: 1,
      y: 0,
    }}
    exit={{
      opacity: 0,
      y: 5,
    }}
    transition={{
      type: 'spring',
      stiffness: 700,
      damping: 40,
    }}
    className="flex justify-around items-center w-full"
  >
    <SelectionCount value={selectedCount} label="Lists" />
    <BulkActionButton
      onClick={onBulkDuplicate}
      icon={Copy}
      label="Duplicate"
      isTinyScreen={isTinyScreen}
      className="text-muted-foreground hover:text-primary"
    />
    <BulkActionButton
      onClick={onBulkDelete}
      icon={Trash2}
      label="Delete"
      isTinyScreen={isTinyScreen}
      className="text-destructive hover:bg-destructive/10"
    />
    <BulkActionButton
      onClick={onClearSelection}
      icon={X}
      label="Cancel"
      isTinyScreen={isTinyScreen}
      className="text-muted-foreground hover:bg-muted"
    />
  </motion.div>
)
const MainNavBar = ({ activeTab, onSelect, onToggleSidebar, isTinyScreen }) => (
  <motion.div
    key="main-nav"
    initial={{
      opacity: 0,
      y: -5,
    }}
    animate={{
      opacity: 1,
      y: 0,
    }}
    exit={{
      opacity: 0,
      y: -5,
    }}
    transition={{
      type: 'spring',
      stiffness: 700,
      damping: 40,
    }}
    className="flex justify-around items-center w-full"
  >
    {TABS.map((tab) => (
      <MobileNavItem
        key={tab.id}
        id={tab.id}
        icon={tab.icon}
        label={tab.label}
        isActive={activeTab === tab.id}
        onSelect={onSelect}
        isTinyScreen={isTinyScreen}
      />
    ))}
  </motion.div>
)
export function MobileNav({
  activeTab,
  onSelect,
  onToggleSidebar,
  className,
  selectedIds,
  todos,
  onBulkComplete,
  onBulkDuplicate,
  onBulkDelete,
  onClearSelection,
  selectedListCount,
  onBulkDuplicateLists,
  onBulkDeleteLists,
  onClearListSelection,
}) {
  const isTinyScreen = useIsTinyScreen()
  const hasTaskSelection = selectedIds && selectedIds.size > 0
  const hasListSelection = selectedListCount !== undefined && selectedListCount > 0
  const isAllDone =
    todos &&
    selectedIds &&
    todos.filter((todo) => selectedIds.has(todo.id)).every((todo) => todo.status === 'done')
  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-t border-border/50 transition-[opacity,transform] duration-300',
        isTinyScreen ? 'px-2 pb-2 pt-1' : 'px-4 pb-6 pt-2',
        className,
      )}
    >
      <div className="flex justify-around items-center min-h-[56px] relative overflow-hidden">
        <AnimatePresence mode="popLayout" initial={false}>
          {hasTaskSelection && (
            <TaskSelectionBar
              selectedCount={selectedIds.size}
              isTinyScreen={isTinyScreen}
              markLabel={isAllDone ? 'Todo' : 'Done'}
              onBulkComplete={onBulkComplete}
              onBulkDuplicate={onBulkDuplicate}
              onBulkDelete={onBulkDelete}
              onClearSelection={onClearSelection}
              markTitle={isAllDone ? 'Reopen' : 'Complete'}
            />
          )}
          {!hasTaskSelection && hasListSelection && (
            <ListSelectionBar
              selectedCount={selectedListCount}
              isTinyScreen={isTinyScreen}
              onBulkDuplicate={onBulkDuplicateLists}
              onBulkDelete={onBulkDeleteLists}
              onClearSelection={onClearListSelection}
            />
          )}
          {!hasTaskSelection && !hasListSelection && (
            <MainNavBar
              activeTab={activeTab}
              onSelect={onSelect}
              onToggleSidebar={onToggleSidebar}
              isTinyScreen={isTinyScreen}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
